import type { AttachmentFileApi } from 'api-typings/posts/posts';
import type {
  LegacyContestAttachmentsUploadResponseAjaxApi,
  LegacyProjectAttachmentsUploadResponseAjaxApi,
  LegacyProjectFilesUploadResponseAjaxApi,
  PortfolioItemsUploadResponseAjaxApi,
} from './file-storage.backend-model';
import type { FileReference, FullMetadata } from './file-storage.model';
import { StorageBucket } from './file-storage.model';

type StorageBackendConfig = {
  [bucket in StorageBucket]: (ref: FileReference) =>
    | {
        // the file upload endpoint pathname
        endpoint: string;
        // is the endpoint AJAX-API (GAF) or REST API
        isGaf?: boolean;
        // is it a legacy backend (that doesn't support multi-part uploads)
        isLegacy: true;
        // the name of the request param holding the uploaded file. This is
        // only not needed for tus uploads.
        fileParam: string;
        // if supported by the storage backend, provide the endpoint to get the
        // file download URL
        downloadURLEndpoint?: string;
        // parse the downloadURL result & returns the download URL itself
        downloadURL?(result: BackendGetDownloadURLReturnTypes[bucket]): string;
        // [DEPRECATED] extra params attached to the request in addition to the
        // file & its metadata, because generic custom metadata are boring so let's
        // build a different backend file service for each variation of data we
        // have
        DEPRECATED_extraParams?(metadata: FullMetadata): {
          [key: string]: string;
        };
        // [DEPRECATED] parse the upload response and returns the MySQL row index
        // storing the file entry because why not (?!).
        // returns the pathname
        DEPREACTED_DO_NOT_USE_backendId?(
          result: BackendUploadReturnTypes[bucket],
        ): number;
        // [DEPRECATED] classic endpoints are boring so let's change the upload
        // and download endpoint URLs based on some arbitrary data to make
        // sure no one will reuse our backend muahahahahah
        DEPRECATED_funkyChangingEndpoint?(metadata: FullMetadata): string;
        DEPRECATED_funkyChangingDownloadUrlEndpoint?(customMetadata: {
          [key: string]: string;
        }): string;
      }
    | {
        isLegacy: false;
        customDownloadUrlEndpoint?(customMetadata: {
          [key: string]: string;
        }): string;
        /** Whether to cache the downloadURL in sessionStorage until it expires */
        urlCacheable?: boolean;
      };
};

export type BackendUploadReturnTypes = {
  [StorageBucket.LEGACY_PROJECT_FILES]: LegacyProjectFilesUploadResponseAjaxApi;
  [StorageBucket.LEGACY_PROJECT_ATTACHMENTS]: LegacyProjectAttachmentsUploadResponseAjaxApi;
  [StorageBucket.PORTFOLIO_ITEMS]: PortfolioItemsUploadResponseAjaxApi;
  [StorageBucket.PROFILE_PICTURES]: {
    avatar_url: string;
  };
  [StorageBucket.LEGACY_CONTEST_ATTACHMENTS]: LegacyContestAttachmentsUploadResponseAjaxApi;
  [StorageBucket.POST_ATTACHMENTS]: {
    attachment_file: AttachmentFileApi;
  };
} & { readonly [k: string]: unknown };

// IMPORTANT: Do not add entries here for non-legacy storage buckets.
// The download URL endpoint of non-legacy storage buckets should
// conform with the response format defined in `NewFileServiceGetDownloadUrlReturnType`
export type BackendGetDownloadURLReturnTypes = {
  [StorageBucket.FL_DRIVE]: {
    readonly download_url: string;
  };
  [StorageBucket.PROFILE_PICTURES]: {
    avatar_url: string;
  };
  [StorageBucket.POST_ATTACHMENTS]: {
    url: string;
  };
  [StorageBucket.MESSAGING_ATTACHMENTS]: {
    download_url: string;
  };
  [StorageBucket.CONTEST_ENTRIES]: {
    files: {
      fileset: string;
      serverName: string;
      originalName: string;
      name: string;
      size: number;
    }[];
  };
  [StorageBucket.CONTEST_HANDOVER_FILES]: {
    filedata: {
      downloadUrl: string;
      fileId: number;
      fileType: string;
      fileset: number;
      name: string;
      size: string;
    };
  };
} & { readonly [k: string]: unknown };

export interface NewFileServiceGetDownloadUrlReturnType {
  url: string;
}

// This allows to configure the myriad of backend file storage services we have
// on a per-bucket basis, the goal being the long term all the buckets should
// use the
export const configs: StorageBackendConfig = {
  [StorageBucket.FL_DRIVE]: ref => ({
    endpoint: `files/0.1/files/${ref.id}`, // FIXME: T267853 - probably wrong
    fileParam: 'files[]',
    downloadURLEndpoint: `files/0.1/files/${ref.id}/download?as_json=true`,
    downloadURL: result => result.download_url,
    isLegacy: true,
  }),
  [StorageBucket.LEGACY_PROJECT_FILES]: ref => ({
    endpoint: 'projects/upload.php',
    isGaf: true,
    isLegacy: true,
    fileParam: 'files[]',
    DEPRECATED_extraParams: metadata => {
      if (!metadata.customMetadata || !metadata.customMetadata.projectId) {
        throw new Error('Missing projectId in custom metadata');
      }
      return {
        'attachments[]': metadata.name,
        project_id: metadata.customMetadata.projectId,
        bid_id: metadata.customMetadata.bidId,
        can_delete: metadata.customMetadata.canDelete,
      };
    },
  }),
  [StorageBucket.LEGACY_PROJECT_ATTACHMENTS]: () => ({
    endpoint: 'projects/uploadTemporaryFile.php',
    isGaf: true,
    fileParam: 'files[]',
    DEPREACTED_DO_NOT_USE_backendId: result => result.fileIds[0],
    isLegacy: true,
  }),
  [StorageBucket.PROFILE_PICTURES]: ref => ({
    endpoint: 'users/0.1/self/profile_picture/',
    fileParam: 'filedata',
    downloadURL: result => result.avatar_url,
    DEPRECATED_extraParams: metadata => {
      if (
        !metadata.customMetadata ||
        !metadata.customMetadata.x ||
        !metadata.customMetadata.y ||
        !metadata.customMetadata.cropW ||
        !metadata.customMetadata.cropH
      ) {
        throw new Error('missing x, y, cropW, or cropH custom metadata');
      }
      return {
        x: metadata.customMetadata.x,
        y: metadata.customMetadata.y,
        cropW: metadata.customMetadata.cropW,
        cropH: metadata.customMetadata.cropH,
        should_detect_face: metadata.customMetadata.shouldDetectFace ?? 'false',
      };
    },
    isLegacy: true,
  }),
  [StorageBucket.PROFILE_COVER_IMAGES]: ref => ({
    endpoint: 'users/0.1/self/cover_image/',
    fileParam: 'filedata',
    DEPRECATED_extraParams: metadata => {
      if (
        !metadata.customMetadata ||
        !metadata.customMetadata.x ||
        !metadata.customMetadata.y ||
        !metadata.customMetadata.crop_width ||
        !metadata.customMetadata.crop_height
      ) {
        throw new Error(
          'missing x, y, crop_width, or crop_height custom metadata',
        );
      }
      return {
        x: metadata.customMetadata.x,
        y: metadata.customMetadata.y,
        crop_width: metadata.customMetadata.crop_width,
        crop_height: metadata.customMetadata.crop_height,
      };
    },
    isLegacy: true,
  }),
  [StorageBucket.GROUP_COVER_IMAGE]: ref => ({
    DEPRECATED_funkyChangingEndpoint: metadata => {
      if (!metadata.customMetadata || !metadata.customMetadata.groupId) {
        throw new Error('missing groupId custom metadata');
      }
      return `groups/0.1/groups/${metadata.customMetadata.groupId}/cover_image/`;
    },
    endpoint: 'this_is_not_used',
    fileParam: 'filedata',
    DEPRECATED_extraParams: metadata => {
      if (
        !metadata.customMetadata ||
        !metadata.customMetadata.x ||
        !metadata.customMetadata.y ||
        !metadata.customMetadata.crop_width ||
        !metadata.customMetadata.crop_height
      ) {
        throw new Error(
          'missing x, y, crop_width, or crop_height custom metadata',
        );
      }
      return {
        x: metadata.customMetadata.x,
        y: metadata.customMetadata.y,
        crop_width: metadata.customMetadata.crop_width,
        crop_height: metadata.customMetadata.crop_height,
      };
    },
    isLegacy: true,
  }),
  [StorageBucket.MESSAGING_ATTACHMENTS]: ref => ({
    endpoint: 'this_is_not_used',
    DEPRECATED_funkyChangingEndpoint: metadata => {
      if (!metadata.customMetadata || !metadata.customMetadata.threadId) {
        throw new Error('missing threadId custom metadata');
      }
      return `messages/0.1/threads/${metadata.customMetadata.threadId}/messages/`;
    },
    fileParam: 'files[]',
    DEPRECATED_extraParams: metadata => {
      if (
        !metadata.customMetadata ||
        !metadata.customMetadata.threadId ||
        !metadata.customMetadata.clientMessageId
      ) {
        throw new Error('missing threadId or clientMessageId custom metadata');
      }
      return {
        thread_id: metadata.customMetadata.threadId,
        client_message_id: metadata.customMetadata.clientMessageId,
      };
    },
    DEPRECATED_funkyChangingDownloadUrlEndpoint: metadata => {
      if (!metadata.messageId) {
        throw new Error(
          'missing messageId custom metadata for messaging attachment',
        );
      }
      return `messages/0.1/messages/${
        metadata.messageId
      }/attachments/${encodeURIComponent(ref.id)}`;
    },
    downloadURL: ({ download_url }) => download_url,
    isLegacy: true,
  }),
  [StorageBucket.MESSAGING_ATTACHMENTS_NEW]: ref => ({
    isLegacy: false,
    urlCacheable: true,
    customDownloadUrlEndpoint: metadata => {
      if (!metadata.messageId) {
        throw new Error(
          'missing messageId custom metadata for messaging attachment',
        );
      }
      if (!metadata.fileUuid) {
        throw new Error(
          'missing fileUuid custom metadata for messaging attachment',
        );
      }

      metadata.messageType =
        metadata.messageType === 'comments' ? 'comments' : 'messages';

      return `messages/0.1/${metadata.messageType}/${metadata.messageId}/attachments_new/${metadata.fileUuid}`;
    },
  }),
  [StorageBucket.SUPERUSER_MESSAGING_ATTACHMENTS]: ref => ({
    // NOTE: files cannot be uploaded to this bucket, only downloaded
    endpoint: 'this_is_not_used',
    fileParam: 'this_is_not_used',
    DEPRECATED_funkyChangingDownloadUrlEndpoint: metadata => {
      if (!metadata.messageId) {
        throw new Error(
          'missing messageId custom metadata for messaging attachment',
        );
      }
      return `superuser/0.1/admin_messages/${
        metadata.messageId
      }/files/${encodeURIComponent(ref.id)}`;
    },
    downloadURL: ({ download_url }) => download_url,
    isLegacy: true,
  }),
  [StorageBucket.PORTFOLIO_ITEMS]: () => ({
    endpoint: `showcase/uploadFile.php`,
    fileParam: 'files[]',
    isGaf: true,
    isLegacy: true,
    DEPREACTED_DO_NOT_USE_backendId: result => result.id,
  }),
  [StorageBucket.LEGACY_CONTEST_ATTACHMENTS]: () => ({
    endpoint: 'contests/uploadTemporaryFile.php',
    isGaf: true,
    isLegacy: true,
    fileParam: 'files[]',
    DEPREACTED_DO_NOT_USE_backendId: result => result.file_ids[0],
  }),
  [StorageBucket.CONTEST_ENTRIES]: () => ({
    endpoint: 'contests/entryFileUpload.php',
    isGaf: true,
    isLegacy: true,
    fileParam: 'upload[]',
    DEPRECATED_extraParams: metadata => {
      if (!metadata.customMetadata || !metadata.customMetadata.projectId) {
        throw new Error('Missing projectId in custom metadata');
      }
      return {
        project_id: metadata.customMetadata.projectId,
      };
    },
    downloadURL: result => result.files[0].serverName,
  }),
  [StorageBucket.CONTEST_HANDOVER_FILES]: () => ({
    endpoint: 'contests/uploadHandoverFile.php',
    fileParam: 'filedata',
    isGaf: true,
    isLegacy: true,
    DEPRECATED_extraParams: metadata => {
      if (!metadata.customMetadata || !metadata.customMetadata.entryId) {
        throw new Error('Missing entryId in custom metadata');
      }
      return {
        entry_id: metadata.customMetadata.entryId,
      };
    },
    downloadURL: result => result.filedata.downloadUrl,
  }),
  [StorageBucket.POST_ATTACHMENTS]: ref => ({
    endpoint: 'posts/0.1/files/',
    fileParam: 'file',
    downloadURLEndpoint: `posts/0.1/files/${ref.id}/download?view_inline=true`,
    downloadURL: result => result.url,
    isLegacy: true,
  }),
  [StorageBucket.GROUPS_POST_ATTACHMENTS]: ref => ({
    isLegacy: false,
    urlCacheable: true,
    customDownloadUrlEndpoint: _ => {
      return `posts/0.1/files/${ref.id}/download?view_inline=true&using_pelican=true`;
    },
  }),
  [StorageBucket.FREELANCER_CV]: ref => ({
    isLegacy: false,
  }),
  [StorageBucket.ENTERPRISE_LOGO]: ref => ({
    isLegacy: false,
    customDownloadUrlEndpoint: _ => {
      return `users/0.1/enterprise/logo/${ref.id}?as_json=true`;
    },
  }),
  [StorageBucket.ENTERPRISE_AVATAR]: ref => ({
    isLegacy: false,
    customDownloadUrlEndpoint: _ => {
      return `users/0.1/enterprise/avatar/${ref.id}?as_json=true`;
    },
  }),
  [StorageBucket.VIDEO_BID]: ref => ({
    isLegacy: false,
    customDownloadUrlEndpoint: _ => {
      return `projects/0.1/bids/pitches/${ref.id}?as_json=true`;
    },
  }),
  [StorageBucket.USER_DOCUMENTS]: ref => ({
    isLegacy: false,
  }),
  [StorageBucket.QUOTATION_LOGOS]: ref => ({
    isLegacy: false,
  }),
  [StorageBucket.TAX_STATEMENT]: ref => ({
    isLegacy: false,
    customDownloadUrlEndpoint: _ => {
      return `payments/0.1/tax_statement/${ref.id}/file?as_json=true`;
    },
  }),
  [StorageBucket.CUSTOM_BANNER]: ref => ({
    isLegacy: false,
    customDownloadUrlEndpoint: _ => {
      return `common/0.1/custom_banners/images/${ref.id}?as_json=true`;
    },
  }),
  [StorageBucket.SERVICE_OFFERING_ATTACHMENTS]: ref => ({
    isLegacy: false,
  }),
  [StorageBucket.NATIVE_E2E_TESTING]: ref => ({
    isLegacy: false,
  }),
};
